// Voter view — what a participant sees at /vote/<token>.

import { ApiError, ensureFeedToken, getMyVote, getVoterSurvey, resolveEditToken, sendMessage, submitVote } from '../api';
import { fetchPowChallenge, solvePow } from '../pow';
import { downloadEditFile } from '../edit-file';
import { downloadReservationIcs } from '../ics-builder';
import { renderResultCard } from './result-card';
import type { Message, VoterSurveyResponse } from '../api';
import type { BusyEvent } from '../ics-parser';
import { generatePseudonym, generateVoterId } from '../pseudonyms';
import { renderVoterCards, renderVoterMatrix } from './voter-matrix';
import { renderCalendarPanel } from './calendar-panel';
import { renderAppHeader } from './app-header';
import { el, clear } from './dom';

type Choice = 'yes' | 'maybe' | 'no';
type Responses = Record<string, Choice>;

type State = {
  voterId: string;
  pseudonym: string;
  responses: Responses;
};

export function renderVoterView(root: HTMLElement, token: string): void {
  const container = el('div', { class: 'voter-view' });
  root.append(renderAppHeader(), container);

  showLoading(container);

  // Edit-Token aus URL? Falls ja, vor allem anderen den Voter rekonstruieren
  // — dann sieht der User auch von einem anderen Gerät seine Stimme.
  const editTokenFromUrl = readEditTokenFromUrl();

  const idKey = `pd:voter_id:${token}`;

  const bootstrap = async (): Promise<string> => {
    if (editTokenFromUrl) {
      try {
        const resolved = await resolveEditToken(token, editTokenFromUrl);
        localStorage.setItem(idKey, resolved.voter_id);
        // Token aus URL entfernen, sobald er konsumiert wurde — nicht in der
        // Browser-History stehen lassen.
        const url = new URL(window.location.href);
        url.searchParams.delete('me');
        window.history.replaceState({}, '', url.toString());
        return resolved.voter_id;
      } catch {
        // ungültiger Token → fall back auf normale Bootstrap-Logik
      }
    }
    let voterId = localStorage.getItem(idKey);
    if (!voterId) {
      voterId = generateVoterId();
      localStorage.setItem(idKey, voterId);
    }
    return voterId;
  };

  bootstrap()
    .then((voterId) => getVoterSurvey(token, voterId).then(async (survey) => {
      const state = await initState(token, voterId, survey);
      showLoaded(container, token, survey, state);
    }))
    .catch((e) => showError(container, e));
}

function readEditTokenFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  const t = params.get('me');
  if (!t) return null;
  if (!/^[a-f0-9]{32}$/.test(t)) return null;
  return t;
}

function showLoading(container: HTMLElement): void {
  clear(container);
  container.append(el('div', { class: 'view-loading' }, 'Lade Umfrage …'));
}

function showError(container: HTMLElement, e: unknown): void {
  clear(container);
  let title = 'Umfrage konnte nicht geladen werden';
  let body = 'Bitte prüfe den Link.';
  if (e instanceof ApiError) {
    if (e.status === 404) {
      title = 'Umfrage nicht gefunden';
      body = 'Der Teilnehmer-Link ist ungültig oder die Umfrage wurde gelöscht.';
    } else if (e.status === 410) {
      title = 'Umfrage ist abgelaufen';
      body = 'Diese Umfrage wurde bereits geschlossen oder gelöscht.';
    }
  } else if (e instanceof Error) {
    body = e.message;
  }
  container.append(
    el('div', { class: 'view-error' },
      el('h2', { class: 'view-error-title' }, title),
      el('p', { class: 'view-error-body' }, body),
    ),
  );
}

async function initState(
  token: string,
  voterId: string,
  _survey: VoterSurveyResponse,
): Promise<State> {
  const nameKey = `pd:pseudonym:${token}`;
  let pseudonym = localStorage.getItem(nameKey);
  if (!pseudonym) {
    pseudonym = generatePseudonym();
    localStorage.setItem(nameKey, pseudonym);
  }

  let responses: Responses = {};
  try {
    const me = await getMyVote(token, voterId);
    if (me.vote) {
      responses = me.vote.responses;
      if (me.vote.pseudonym) {
        pseudonym = me.vote.pseudonym;
        localStorage.setItem(nameKey, pseudonym);
      }
    }
  } catch {
    // ignore — start fresh
  }

  return { voterId, pseudonym, responses };
}

function showLoaded(
  container: HTMLElement,
  token: string,
  survey: VoterSurveyResponse,
  state: State,
): void {
  clear(container);
  const titleText = survey.title?.trim() || 'Slot Umfrage';
  const nowSec = Math.floor(Date.now() / 1000);
  const deadlinePassed = survey.voting_deadline !== null && survey.voting_deadline <= nowSec;
  const isClosed = survey.closed_at !== null || deadlinePassed;
  const isFinalized = (survey.final_slot_ids?.length ?? 0) > 0;

  // Result-Card oben, wenn der Admin finalisiert hat. Hurra-Konfetti einmal
  // pro Session pro Survey beim Erst-Aufruf nach Finalisierung.
  if (isFinalized) {
    const finalSlots = survey.slots.filter((s) => survey.final_slot_ids!.includes(s.id));
    container.append(renderResultCard({
      surveyId: survey.id,
      title: survey.title,
      voterToken: survey.voter_token,
      finalSlots,
      finalMessage: survey.final_message,
      location: survey.location,
      audience: 'voter',
    }));
  }

  container.append(
    el('header', { class: 'voter-header' },
      el('h1', { class: 'voter-title' }, titleText),
      ...(survey.location ? [renderLocationLine(survey.location)] : []),
      el(
        'p',
        { class: 'voter-subtitle' },
        isFinalized
          ? 'So haben alle abgestimmt:'
          : isClosed
            ? 'Diese Umfrage ist geschlossen. Du kannst keine Stimme mehr abgeben.'
            : 'Markiere für jeden Termin, ob er für dich passt.',
      ),
      el(
        'p',
        { class: 'voter-privacy-note' },
        '🔒 Dein Pseudonym und (falls importiert) dein Kalender bleiben in deinem Browser. Wir speichern keine Identitätsdaten.',
      ),
    ),
  );

  // Deadline-Banner — Countdown solange offen, statisch wenn vorbei.
  if (survey.voting_deadline !== null && !isFinalized) {
    container.append(renderDeadlineBanner(survey.voting_deadline));
  }

  // Pseudonym
  container.append(renderPseudonymSection(token, state, survey.slots, isClosed, survey.title));

  // Voter's calendar — purely browser-side.
  let busyEvents: BusyEvent[] = [];
  const calendarPanel = renderCalendarPanel({
    onUpdate: (events) => {
      busyEvents = events;
      remountMatrix();
    },
  });
  container.append(calendarPanel);

  // Save status
  const status = el('div', { class: 'voter-status' });
  container.append(status);

  // Progress (live updated)
  const progress = el('footer', { class: 'voter-progress' });
  updateProgress(progress, state.responses, survey.slots.length);

  // Voter matrix — re-mounted whenever the calendar changes so busy overlays
  // appear/disappear without a full page reload.
  const matrixHost = el('div', { class: 'voter-matrix-host' });

  const onChange = () => {
    updateProgress(progress, state.responses, survey.slots.length);
    persistDebounced(token, state, survey.slots, status, survey.title);
  };

  const remountMatrix = () => {
    clear(matrixHost);
    const sharedOpts = {
      config: survey.config,
      slots: survey.slots,
      responses: state.responses,
      isClosed,
      busyEvents,
      aggregate: survey.vote_aggregate,
      onChange,
    };
    // Beide Views ins DOM — CSS @media zeigt nur die passende.
    matrixHost.append(renderVoterMatrix(sharedOpts));
    matrixHost.append(renderVoterCards(sharedOpts));
  };

  remountMatrix();
  container.append(matrixHost);
  container.append(progress);

  // Reservierungs-iCal: trägt die Wahl-Termine als belegt-Blöcke (oder
  // tentativ bei „vielleicht") in den eigenen Kalender ein. Erscheint, sobald
  // mindestens eine Ja/Vielleicht-Antwort gesetzt ist.
  container.append(renderReservationSection(token, state, survey));

  // Messages
  container.append(renderMessagesSection(token, state, survey.messages, isClosed));

  // Marketing-CTA — Voter ist der wichtigste Spreader. Nach der Stimmabgabe
  // ein freundlicher Hinweis, selbst eine Umfrage anzulegen.
  container.append(renderSpreaderCta());

  if (Object.keys(state.responses).length > 0) {
    status.classList.add('is-info');
    status.textContent = 'Gespeicherte Antworten geladen.';
  }
}

/** Reservierungs-Sektion: lädt die YES/MAYBE-Slots als .ics-Datei in den
 *  Kalender des Voters. Updatet sich bei Response-Änderungen. */
function renderReservationSection(
  voterToken: string,
  state: State,
  survey: VoterSurveyResponse,
): HTMLElement {
  const section = el('section', { class: 'voter-reservation-section' });
  const heading = el('h2', { class: 'voter-reservation-heading' }, '📅 In meinen Kalender');
  const desc = el('p', { class: 'voter-reservation-desc' });
  const button = el('button',
    { type: 'button', class: 'btn-secondary voter-reservation-btn' },
    '⬇ Als .ics herunterladen',
  ) as HTMLButtonElement;
  const liveBtn = el('button',
    { type: 'button', class: 'btn-secondary voter-reservation-btn voter-reservation-live' },
    '🔄 Live-Verbindung',
  ) as HTMLButtonElement;
  liveBtn.title = 'Calendar-Subscription — bleibt automatisch synchron, wenn der Admin den Termin fixiert';
  const note = el('p', { class: 'voter-reservation-note' },
    '🔒 Download wird nur in deinem Browser erzeugt. Live-Verbindung: Slot-Server liefert ein dynamisches Calendar-Abo, ',
    'das sich automatisch auf den finalen Termin reduziert.',
  );

  const update = () => {
    const yes = Object.values(state.responses).filter((v) => v === 'yes').length;
    const maybe = Object.values(state.responses).filter((v) => v === 'maybe').length;
    const total = yes + maybe;
    if (total === 0) {
      section.style.display = 'none';
      return;
    }
    section.style.display = '';
    const parts: string[] = [];
    if (yes > 0)   parts.push(`${yes} ${yes === 1 ? 'fester Termin' : 'feste Termine'}`);
    if (maybe > 0) parts.push(`${maybe} ${maybe === 1 ? 'tentativer Termin' : 'tentative Termine'}`);
    desc.textContent = parts.join(' · ') + ' werden geblockt.';
    button.textContent = total === 1
      ? '⬇ Reservierung als .ics herunterladen'
      : `⬇ ${total} Termine als .ics herunterladen`;
  };

  button.addEventListener('click', () => {
    downloadReservationIcs({
      surveyTitle: survey.title,
      surveyLocation: survey.location,
      slots: survey.slots,
      responses: state.responses,
      voterToken,
    });
  });

  liveBtn.addEventListener('click', async () => {
    liveBtn.disabled = true;
    const orig = liveBtn.textContent;
    liveBtn.textContent = '…';
    try {
      const res = await ensureFeedToken(voterToken, state.voterId);
      const url = `${window.location.origin}${res.feed_url}`;
      // Calendar-Subscription per webcal:-Schema, damit z.B. Apple-Calendar
      // gleich beim Klick „Subscribe?" anbietet.
      const webcalUrl = url.replace(/^https?:/, 'webcal:');
      const dialog = window.confirm(
        'Live-Verbindung erzeugt!\n\n' +
        'URL:\n' + url + '\n\n' +
        'OK: Direkt im Standard-Kalender öffnen (webcal://)\n' +
        'Abbrechen: nur die URL kopieren',
      );
      if (dialog) {
        window.location.href = webcalUrl;
      } else {
        try { await navigator.clipboard.writeText(url); } catch { window.prompt('Subscription-URL', url); }
      }
    } catch (e) {
      window.alert('Live-Verbindung fehlgeschlagen: ' + (e instanceof Error ? e.message : 'unbekannt'));
    } finally {
      liveBtn.disabled = false;
      liveBtn.textContent = orig;
    }
  });

  // Re-render bei jedem Click in der Matrix — Polling reicht, weil onChange
  // im Matrix-Modul nicht direkt aus hier zugänglich ist.
  setInterval(update, 800);

  const buttonRow = el('div', { class: 'voter-reservation-buttons' }, button, liveBtn);
  section.append(heading, desc, buttonRow, note);
  update();
  return section;
}

/** Kleiner Spreader-CTA am Ende der Voter-View — „Mach selber eine Umfrage". */
function renderSpreaderCta(): HTMLElement {
  const sec = el('section', { class: 'voter-spreader-cta' });
  sec.append(
    el('div', { class: 'voter-spreader-text' },
      el('strong', {}, 'Möchtest du selber eine Umfrage anlegen?'),
      ' Slot ist privacy-first: keine Konten, keine Mail-Adressen, kein Tracking. Drei Klicks bis zum Teilen.',
    ),
    el('a', {
      href: '/new',
      class: 'btn-primary voter-spreader-btn',
    }, '+ Eigene Umfrage'),
  );
  return sec;
}

function renderPseudonymSection(
  token: string,
  state: State,
  slots: import('../types').Appointment[],
  isClosed: boolean,
  surveyTitle: string | null = null,
): HTMLElement {
  const section = el('section', { class: 'voter-name-section' });
  section.append(
    el('label', { class: 'voter-name-label', for: 'voter-name-input' }, 'Dein Name in dieser Umfrage'),
  );

  const input = el('input', {
    type: 'text',
    id: 'voter-name-input',
    class: 'voter-name-input',
    value: state.pseudonym,
    maxlength: 60,
  }) as HTMLInputElement;
  if (isClosed) input.disabled = true;

  const regen = el(
    'button',
    {
      type: 'button',
      class: 'btn-secondary voter-name-regen',
      title: 'Anderes Pseudonym vorschlagen',
    },
    '↻ Anderer Vorschlag',
  ) as HTMLButtonElement;
  if (isClosed) regen.disabled = true;
  regen.addEventListener('click', () => {
    const next = generatePseudonym();
    input.value = next;
    state.pseudonym = next;
    localStorage.setItem(`pd:pseudonym:${token}`, next);
    if (Object.keys(state.responses).length > 0) {
      persistDebounced(token, state, slots, undefined, surveyTitle);
    }
  });

  input.addEventListener('change', () => {
    const trimmed = input.value.trim();
    if (trimmed === '') {
      input.value = state.pseudonym;
      return;
    }
    state.pseudonym = trimmed;
    localStorage.setItem(`pd:pseudonym:${token}`, trimmed);
    if (Object.keys(state.responses).length > 0) {
      persistDebounced(token, state, slots, undefined, surveyTitle);
    }
  });

  const row = el('div', { class: 'voter-name-row' }, input, regen);
  section.append(row);
  return section;
}

function renderMessagesSection(
  token: string,
  state: State,
  initialMessages: Message[],
  isClosed: boolean,
): HTMLElement {
  const section = el('section', { class: 'voter-messages-section' });
  section.append(
    el('h2', { class: 'panel-heading' }, 'Nachrichten'),
    el('p', { class: 'voter-messages-intro' },
      'Schreib dem Admin und/oder den anderen Teilnehmern. ',
      el('strong', {}, 'Nur an Admin'),
      ' bedeutet: keiner außer dem Organisator sieht die Nachricht.',
    ),
  );

  // Form
  const form = el('div', { class: 'voter-messages-form' });
  const textarea = el('textarea', {
    class: 'voter-message-input',
    placeholder: 'Deine Nachricht …',
    rows: 2,
    maxlength: 2000,
  }) as HTMLTextAreaElement;
  if (isClosed) textarea.disabled = true;

  // Visibility radio
  const visGroup = el('div', { class: 'voter-message-visibility', role: 'radiogroup' });
  const radioPublic = el('input', {
    type: 'radio',
    name: 'msg-vis',
    value: 'public',
    id: 'msg-vis-public',
    checked: 'checked',
  }) as HTMLInputElement;
  const radioAdmin = el('input', {
    type: 'radio',
    name: 'msg-vis',
    value: 'admin',
    id: 'msg-vis-admin',
  }) as HTMLInputElement;
  visGroup.append(
    el('label', { class: 'voter-message-vis-label', for: 'msg-vis-public' },
      radioPublic, ' 📣 Für alle sichtbar'),
    el('label', { class: 'voter-message-vis-label', for: 'msg-vis-admin' },
      radioAdmin, ' 🔒 Nur für den Admin'),
  );

  const sendBtn = el('button',
    { type: 'button', class: 'btn-primary voter-message-send' },
    'Senden',
  ) as HTMLButtonElement;
  if (isClosed) sendBtn.disabled = true;

  const sendStatus = el('p', { class: 'voter-message-status' });
  sendStatus.style.display = 'none';

  form.append(textarea, visGroup, sendBtn, sendStatus);
  section.append(form);

  // Messages list
  const list = el('ul', { class: 'voter-message-list' });
  const renderList = (messages: Message[]) => {
    clear(list);
    if (messages.length === 0) {
      list.append(el('li', { class: 'voter-message-empty' },
        'Noch keine Nachrichten. Sei die Erste / der Erste!'));
      return;
    }
    for (const msg of messages) {
      list.append(renderMessageItem(msg));
    }
  };
  renderList(initialMessages);
  section.append(list);

  // Local message buffer so we can append optimistically.
  const messages: Message[] = [...initialMessages];

  sendBtn.addEventListener('click', async () => {
    const text = textarea.value.trim();
    if (text === '') {
      sendStatus.textContent = 'Bitte zuerst eine Nachricht eintippen.';
      sendStatus.classList.remove('is-info');
      sendStatus.classList.add('is-error');
      sendStatus.style.display = '';
      return;
    }
    const visibility: 'public' | 'admin' = radioAdmin.checked ? 'admin' : 'public';
    sendBtn.disabled = true;
    sendBtn.textContent = '…';
    sendStatus.style.display = 'none';
    try {
      const created = await sendMessage(token, {
        voter_id: state.voterId,
        pseudonym: state.pseudonym,
        text,
        visibility,
      });
      messages.push({ ...created, is_own: true });
      renderList(messages);
      textarea.value = '';
      sendStatus.textContent = visibility === 'admin'
        ? '✓ Nachricht (privat) gesendet.'
        : '✓ Nachricht gesendet.';
      sendStatus.classList.remove('is-error');
      sendStatus.classList.add('is-info');
      sendStatus.style.display = '';
    } catch (e) {
      sendStatus.textContent = e instanceof Error
        ? `Fehler: ${e.message}`
        : 'Fehler beim Senden.';
      sendStatus.classList.remove('is-info');
      sendStatus.classList.add('is-error');
      sendStatus.style.display = '';
    } finally {
      sendBtn.disabled = isClosed;
      sendBtn.textContent = 'Senden';
    }
  });

  return section;
}

function renderMessageItem(msg: Message): HTMLElement {
  const item = el('li', { class: 'voter-message-item' });
  if (msg.visibility === 'admin') item.classList.add('is-private');
  if (msg.is_own) item.classList.add('is-own');

  const header = el('div', { class: 'voter-message-header' });
  header.append(el('span', { class: 'voter-message-pseudo' }, msg.pseudonym));
  if (msg.visibility === 'admin') {
    header.append(el('span', { class: 'voter-message-badge' }, '🔒 nur Admin'));
  }
  header.append(el('span', { class: 'voter-message-time' }, formatRelative(msg.created_at)));
  item.append(header);

  // Whitespace-preserving text. We don't render any HTML — XSS-safe by default.
  const body = el('p', { class: 'voter-message-text' }, msg.text);
  item.append(body);

  return item;
}

/** Deadline-Banner: zeigt Countdown solange offen, „Abstimmung beendet" sonst.
 *  Aktualisiert sich pro Minute via setInterval. Reload nach Ablauf, damit
 *  der Server-Lock im UI sichtbar wird (Matrix → disabled). */
function renderDeadlineBanner(deadlineUnix: number): HTMLElement {
  const banner = el('div', { class: 'voter-deadline-banner' });
  const update = () => {
    const now = Math.floor(Date.now() / 1000);
    const left = deadlineUnix - now;
    if (left <= 0) {
      banner.classList.add('is-expired');
      banner.textContent = '⏱ Abstimmung beendet — keine neuen Stimmen mehr.';
      return false;
    }
    banner.classList.remove('is-expired');
    banner.textContent = `⏱ Abstimmung endet in ${formatDuration(left)} (${formatDeadlineAbsolute(deadlineUnix)})`;
    return true;
  };
  if (!update()) return banner;
  // Pro Minute reicht — die letzten 60 Sekunden lassen wir sekündlich laufen.
  let timer: ReturnType<typeof setInterval> | null = null;
  const tick = () => {
    const left = deadlineUnix - Math.floor(Date.now() / 1000);
    if (timer) clearInterval(timer);
    if (left <= 0) {
      update();
      // Auto-Reload, damit isClosed = true greift und die Matrix sperrt.
      window.setTimeout(() => window.location.reload(), 1500);
      return;
    }
    timer = setInterval(() => {
      const ok = update();
      if (!ok) {
        if (timer) clearInterval(timer);
        window.setTimeout(() => window.location.reload(), 1500);
      } else {
        // Wenn weniger als 60s übrig, sekündlich tickern lassen.
        const remaining = deadlineUnix - Math.floor(Date.now() / 1000);
        if (remaining < 60 && (timer as unknown as { _interval?: number })._interval !== 1000) {
          if (timer) clearInterval(timer);
          timer = setInterval(update, 1000);
        }
      }
    }, deadlineUnix - Math.floor(Date.now() / 1000) < 60 ? 1000 : 30_000);
  };
  tick();
  return banner;
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds} Sek`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} Min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const m = minutes % 60;
    return m > 0 ? `${hours} Std ${m} Min` : `${hours} Std`;
  }
  const days = Math.floor(hours / 24);
  const h = hours % 24;
  return h > 0 ? `${days} Tag${days === 1 ? '' : 'e'} ${h} Std` : `${days} Tag${days === 1 ? '' : 'e'}`;
}

function formatDeadlineAbsolute(unix: number): string {
  const d = new Date(unix * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}. ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Survey-Location als 📍-Zeile. URL → klickbarer Link, sonst Klartext.
 *  Akzeptiert nur http(s)://; alles andere bleibt Text — Schutz vor
 *  z.B. javascript:-URLs. */
function renderLocationLine(location: string): HTMLElement {
  const trimmed = location.trim();
  const isUrl = /^https?:\/\//i.test(trimmed);
  const p = el('p', { class: 'voter-location-line' }, '📍 ');
  if (isUrl) {
    p.append(el('a', {
      href: trimmed,
      target: '_blank',
      rel: 'noopener noreferrer',
      class: 'voter-location-link',
    }, trimmed));
  } else {
    p.append(trimmed);
  }
  return p;
}

function formatRelative(unixSeconds: number): string {
  const diffSec = Math.floor(Date.now() / 1000) - unixSeconds;
  if (diffSec < 60) return 'gerade eben';
  if (diffSec < 3600) return `vor ${Math.floor(diffSec / 60)} min`;
  if (diffSec < 86400) return `vor ${Math.floor(diffSec / 3600)} h`;
  const d = new Date(unixSeconds * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}. ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function updateProgress(
  footer: HTMLElement,
  responses: Responses,
  total: number,
): void {
  const answered = Object.keys(responses).length;
  const unanswered = total - answered;
  if (unanswered === 0) {
    footer.textContent = `Alle ${total} Termine beantwortet ✓`;
  } else {
    footer.textContent =
      `${answered} von ${total} angeklickt — die restlichen ${unanswered} ` +
      `zählen als ✗ Nein.`;
  }
}

let saveTimer: number | null = null;

function persistDebounced(
  token: string,
  state: State,
  slots: import('../types').Appointment[],
  status?: HTMLElement,
  surveyTitle?: string | null,
): void {
  if (saveTimer !== null) clearTimeout(saveTimer);
  saveTimer = window.setTimeout(() => {
    saveTimer = null;
    void doPersist(token, state, slots, status, surveyTitle);
  }, 250);
}

async function doPersist(
  token: string,
  state: State,
  slots: import('../types').Appointment[],
  status?: HTMLElement,
  surveyTitle?: string | null,
): Promise<void> {
  if (status) {
    status.textContent = '🔐 Anti-Spam-Rechnung läuft …';
    status.classList.remove('is-info', 'is-error');
    status.classList.add('is-saving');
  }
  try {
    // Proof-of-Work — bremst Skripte. Browser rechnet ~0.5–1 s.
    const challenge = await fetchPowChallenge();
    const nonce = await solvePow(challenge.challenge, challenge.difficulty);
    if (status) status.textContent = 'Speichere …';
    // Nicht angeklickte Slots zählen als „Nein". Das matched die rote
    // Voter-Visual und macht Admin-Auswertung deterministisch (kein „keine
    // Antwort" mehr — entweder ja, vielleicht oder nein).
    const completeResponses: Responses = {};
    for (const s of slots) {
      completeResponses[s.id] = state.responses[s.id] ?? 'no';
    }
    const result = await submitVote(token, {
      voter_id: state.voterId,
      pseudonym: state.pseudonym,
      responses: completeResponses,
      pow_challenge: challenge.challenge,
      pow_nonce: nonce,
    });
    if (status) {
      status.textContent = '✓ Gespeichert';
      status.classList.remove('is-saving', 'is-error');
      status.classList.add('is-info');
    }
    // Edit-Token kommt nur beim ALLERERSTEN Submit zurück (Server speichert
    // danach nur den Hash). Hier dem Voter die drei Save-Optionen anbieten,
    // damit er später auch von einem anderen Gerät editieren kann.
    if (result.edit_token) {
      showEditLinkOptions(token, result.edit_token, surveyTitle ?? null);
    }
  } catch (e) {
    if (status) {
      status.textContent =
        e instanceof Error ? `Fehler beim Speichern: ${e.message}` : 'Fehler beim Speichern.';
      status.classList.remove('is-saving', 'is-info');
      status.classList.add('is-error');
    }
  }
}

/** Modal mit drei Optionen zum Speichern des persönlichen Edit-Links. */
function showEditLinkOptions(voterToken: string, editToken: string, surveyTitle: string | null): void {
  const editUrl = `${window.location.origin}/vote/${voterToken}?me=${editToken}`;
  const overlay = el('div', { class: 'modal-overlay', role: 'dialog', 'aria-modal': 'true' });
  const dialog = el('div', { class: 'modal-dialog edit-link-dialog' });
  dialog.append(
    el('h2', { class: 'modal-heading' }, '🔑 Dein persönlicher Bearbeitungs-Link'),
    el('p', { class: 'modal-intro' },
      'Du hast deine Stimme abgegeben — ',
      el('strong', {}, 'glückwunsch!'),
      ' Wenn du sie später von einem ',
      el('em', {}, 'anderen Gerät'),
      ' aus ändern willst, brauchst du diesen Link. ',
      el('strong', {}, 'Behalte ihn für dich'),
      ' — wer ihn hat, kann deine Stimme verändern.',
    ),
    el('div', { class: 'modal-url-box' },
      (() => {
        const inp = el('input', { type: 'text', class: 'modal-url-input', readonly: 'readonly', value: editUrl }) as HTMLInputElement;
        inp.addEventListener('focus', () => inp.select());
        return inp;
      })(),
    ),
    el('div', { class: 'edit-link-actions' },
      makeOptionButton('🔖 Lesezeichen anlegen', 'Drück auf deinem Browser ⭐ oder Cmd/Ctrl+D — der Link landet in deinen Lesezeichen.', () => {
        window.alert('Drück jetzt ⭐ in deinem Browser, oder Cmd+D (Mac) / Strg+D (Windows), um diese Seite als Lesezeichen zu speichern. Achtung: speichere die NEU geöffnete Seite nicht als Lesezeichen — die hat den Token noch in der URL.');
      }),
      makeOptionButton('📋 Link kopieren', 'Legt den Link in die Zwischenablage. Schick ihn dir selbst per Mail oder pack ihn in deine Notizen.', async () => {
        try {
          await navigator.clipboard.writeText(editUrl);
        } catch {
          // ignore
        }
      }),
      makeOptionButton('💾 Als HTML-Datei herunterladen', 'Schlichte Datei mit einem dicken Knopf — Doppelklick öffnet sie und der Knopf führt zurück.', () => {
        downloadEditFile({ title: surveyTitle, origin: window.location.origin, voterToken, editToken });
      }),
    ),
    el('p', { class: 'edit-link-skip' },
      'Wenn du nichts davon machst, wird Slot dich nur auf diesem Gerät erkennen (über den Browser-Speicher). ',
      'Du kannst diesen Dialog jetzt schließen.',
    ),
    (() => {
      const closeBtn = el('button', { type: 'button', class: 'btn-primary' }, 'Schließen');
      closeBtn.addEventListener('click', () => overlay.remove());
      return closeBtn;
    })(),
  );
  overlay.append(dialog);
  document.body.append(overlay);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) overlay.remove();
  });
}

function makeOptionButton(label: string, hint: string, action: () => void): HTMLElement {
  const wrap = el('div', { class: 'edit-link-option' });
  const btn = el('button', { type: 'button', class: 'btn-secondary edit-link-option-btn' }, label) as HTMLButtonElement;
  let resetTimer: ReturnType<typeof setTimeout> | null = null;
  btn.addEventListener('click', () => {
    action();
    btn.textContent = '✓ Erledigt';
    if (resetTimer) clearTimeout(resetTimer);
    resetTimer = setTimeout(() => { btn.textContent = label; }, 2200);
  });
  wrap.append(btn, el('p', { class: 'edit-link-option-hint' }, hint));
  return wrap;
}
